import { projectIconSchema } from '../icon'
import { nanoidSchema } from '@/utility'
import { nanoid } from 'nanoid'
import { z, type ZodSchema, type ZodTypeDef } from 'zod'

export type Children = string[]

export enum SidebarType {
  Page = 'Page',
  Folder = 'Folder',
  Link = 'Link',
}

const baseSidebarItemSchema = z.object({
  uid: nanoidSchema,
  title: z.string().trim().min(1),
  type: z.nativeEnum(SidebarType),
  link: z.string().optional(),
  icon: projectIconSchema.optional(),
  children: z.string().array().optional(),
  description: z.string().optional(),
  show: z.boolean(),
})

/** Base sidebar item. Links, Pages, and Folders will all extend this schema */
export type BaseSidebarItem = z.infer<typeof baseSidebarItemSchema>

// ---------------------------------------------------------------------------

const linkSchema = baseSidebarItemSchema.extend({
  title: z.preprocess(
    (arg) => (arg as string)?.trim() || 'Link',
    z.string().trim().min(1),
  ),
  link: z.string(),
  type: z.literal(SidebarType.Link),
})

/** Link sidebar item */
export type Link = z.infer<typeof linkSchema>

// ---------------------------------------------------------------------------

const pageSchema = baseSidebarItemSchema.extend({
  title: z.preprocess(
    (arg) => (arg as string)?.trim() || 'Page',
    z.string().trim().min(1),
  ),
  description: z.string(),
  children: z.string().array().default([]),
  type: z.literal(SidebarType.Page),
  yjsReference: z.string(),
  backgroundImage: z.string().optional(),
  icon: projectIconSchema.optional(),
})

/** Page sidebar item */
export type Page = z.infer<typeof pageSchema>

export const defaultPage = (): Page => ({
  uid: nanoid(),
  title: 'Getting Started',
  type: SidebarType.Page,
  description: 'Start writing your API Documentation with our platform',
  children: [],
  show: true,
  yjsReference: nanoid(),
})

// ---------------------------------------------------------------------------

const folderSchema = baseSidebarItemSchema.extend({
  title: z.preprocess(
    (arg) => (arg as string)?.trim() || 'Folder',
    z.string().trim().min(1),
  ),
  children: z.string().array().default([]),
  type: z.literal(SidebarType.Folder),
})

/** Folder sidebar item */
export type Folder = z.infer<typeof folderSchema>

// ---------------------------------------------------------------------------

export const sidebarItemSchema = z.union([
  linkSchema,
  pageSchema,
  folderSchema,
]) satisfies ZodSchema<SidebarItem, ZodTypeDef, any>

export type SidebarItem = Link | Page | Folder

// ---------------------------------------------------------------------------

export const sidebarSchema = z
  .object({
    /** Complete record of ALL sidebar items */
    items: z.record(z.string(), sidebarItemSchema),
    /** Ordered list of the direct children */
    children: z.string().array(),
  })
  .transform((val) => {
    // Ensure at least 1 page exists for a guide
    const hasPage = Object.values(val.items).some(
      (item) => item.type === SidebarType.Page,
    )
    if (!hasPage) {
      const firstPage = defaultPage()
      val.items[firstPage.uid] = firstPage
      val.children.push(firstPage.uid)
    }

    return val
  })

export type Sidebar = z.infer<typeof sidebarSchema>
